import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import {
    chatAPI, llmConfigAPI, ragActionsAPI,
    ChatSendParams, ChatSendResponse, ChatMessage,
    ChatSessionSummary, ChatSession,
    LLMConfigInfo, LLMConfigSaveParams, LLMProviderInfo,
    ModelDiscoveryResult,
    KnowledgeMode, ActionExecutionResult,
    ChatSendError, ChatSendErrorCode,
} from '../../services/chatAPI';
import { ownKeyAllowed, type AssistantUsageInfo } from '../../services/assistantUsage';
import { LOADING_STATES } from '../../constants';
import type { RootState } from '../../app/store';
import type { OrigineDuPlateau } from '../../components/rag/chatOpenEvent';
import type { MetaNotesState } from '../metanotes/metanotesSlice';

/** Supprime les blocs ACTION_PROPOSAL de la réponse (défense frontend) */
function stripActionProposalBlocks(text: string): string {
    if (!text.includes('ACTION_PROPOSAL')) return text;
    return text
        .replace(/<<<ACTION_PROPOSAL>>>[\s\S]*?<<<\/ACTION_PROPOSAL>>>/g, '')
        .replace(/<<<ACTION_PROPOSAL>>>[\s\S]*/g, '')
        .replace(/<<<\/?ACTION_PROPOSAL>>>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// ─── Default Providers (fallback client-side) ─────────────────────────

const DEFAULT_LLM_PROVIDERS: LLMProviderInfo[] = [
    {
        id: 'openai',
        label: 'OpenAI',
        // Même liste que le repli serveur (`LLMProviderFactory.FALLBACK_PROVIDERS`), alignée le 23/09/2026 :
        // le dialogue l'affiche avant la réponse de `GET /api/llm-config/providers`.
        defaultModel: 'gpt-4.1-mini',
        models: ['gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini', 'o3', 'o3-mini', 'o4-mini'],
    },
    {
        id: 'anthropic',
        label: 'Anthropic',
        defaultModel: 'claude-sonnet-5',
        models: ['claude-opus-5', 'claude-opus-4-8', 'claude-sonnet-5', 'claude-sonnet-4-6', 'claude-haiku-4-5'],
    },
    {
        id: 'google',
        label: 'Google Gemini',
        defaultModel: 'gemini-2.5-flash',
        models: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.0-flash', 'gemini-1.5-pro'],
    },
    {
        id: 'mistral',
        label: 'Mistral AI',
        defaultModel: 'mistral-small-latest',
        // Même liste que le repli serveur (`LLMProviderFactory`), vérifiée le 22/09/2026
        models: ['mistral-medium-latest', 'mistral-small-latest', 'ministral-14b-latest', 'ministral-8b-latest', 'codestral-latest'],
    },
];

// ─── Types ────────────────────────────────────────────────────────────

/**
 * La portée de la conversation personnelle : « Demander sur ce document » (C19, les extraits du
 * document sont cherchés en priorité) ou, depuis C26 SP4, les sources d'une organisation dont je
 * suis membre (le serveur compose ses passes ; le client n'envoie que l'identifiant).
 */
export type ChatScope =
    | { kind: 'document'; fileId: string; fileName: string }
    | { kind: 'organisation'; organisationId: string; nom: string };

export interface ChatState {
    /** Panel ouvert/fermé */
    isOpen: boolean;
    /** Portée du document (C19) ; suit la fenêtre, pas la conversation. */
    scope: ChatScope | null;
    /** L'îlot du plateau d'où la conversation est partie (C28 SP3) ; effacée à la fermeture du panneau et au changement de session. */
    origine: OrigineDuPlateau | null;
    /** Liste des sessions (résumés) */
    sessions: ChatSessionSummary[];
    sessionsTotal: number;
    sessionsStatus: string;
    /** Session active */
    activeSessionId: string | null;
    activeMessages: ChatMessage[];
    activeTitle: string;
    /** Envoi de message en cours */
    sendStatus: string;
    sendError?: string;
    /** Code de la dernière erreur d'envoi (C11 SP4) — le mobile en tire son message ; absent si inconnu */
    sendErrorCode?: ChatSendErrorCode;
    /** Détails du contingent atteint (C18) — présent avec `sendErrorCode === 'quota_exceeded'` */
    sendErrorUsage?: AssistantUsageInfo;
    /** Dernière réponse reçue */
    lastAnswer?: string;
    lastStats?: ChatSendResponse['stats'];
    /** Config LLM */
    llmConfig: LLMConfigInfo | null;
    llmConfigStatus: string;
    llmProviders: LLMProviderInfo[];
    llmValidating: boolean;
    llmValidationResult?: { valid: boolean; error?: string };
    /** Modèles découverts dynamiquement via l'API du provider (clé de l'utilisateur) */
    llmModels: ModelDiscoveryResult | null;
    llmModelsStatus: string;
    /** Mode de connaissance : strict (défaut), enriched, free */
    knowledgeMode: KnowledgeMode;
    /** Mode écriture RAG */
    writeMode: boolean;
    /** ID de la proposition en cours d'exécution */
    executingProposalId: string | null;
}

const initialState: ChatState = {
    isOpen: false,
    scope: null,
    origine: null,
    sessions: [],
    sessionsTotal: 0,
    sessionsStatus: LOADING_STATES.IDLE,
    activeSessionId: null,
    activeMessages: [],
    activeTitle: '',
    sendStatus: LOADING_STATES.IDLE,
    sendError: undefined,
    sendErrorCode: undefined,
    sendErrorUsage: undefined,
    llmConfig: null,
    llmConfigStatus: LOADING_STATES.IDLE,
    llmProviders: DEFAULT_LLM_PROVIDERS,
    llmValidating: false,
    llmModels: null,
    llmModelsStatus: LOADING_STATES.IDLE,
    knowledgeMode: 'strict',
    writeMode: false,
    executingProposalId: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────

/** Envoie un message au pipeline RAG */
export const sendChatMessage = createAsyncThunk<
    ChatSendResponse,
    ChatSendParams & { instance: any; displayQuery?: string },
    // `rejectValue` reste une chaîne (le bureau l'affiche telle quelle) ; le
    // code de l'erreur voyage par le meta du rejet (C11 SP4).
    { state: RootState; rejectValue: string; rejectedMeta: { code?: ChatSendErrorCode; usage?: AssistantUsageInfo } }
>(
    'chat/sendMessage',
    // `signal` : AbortController du thunk — `dispatch(...).abort()` (bouton
    // « Arrêter ») annule la requête HTTP en cours, pas seulement le thunk.
    async ({ instance, displayQuery, ...params }, { rejectWithValue, signal }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token', { code: 'session_auth' });
            return await chatAPI.sendMessage({ ...params, displayQuery }, token, signal);
        } catch (error: any) {
            const typed = error instanceof ChatSendError ? error : undefined;
            return rejectWithValue(error.message || 'Erreur d\'envoi', { code: typed?.code, usage: typed?.usage });
        }
    }
);

/** Charge la liste des sessions. `append: true` = page suivante (Charger plus) */
export const loadChatSessions = createAsyncThunk<
    { sessions: ChatSessionSummary[]; total: number },
    { instance: any; limit?: number; offset?: number; append?: boolean; organisationId?: string },
    { state: RootState; rejectValue: string }
>(
    'chat/loadSessions',
    async ({ instance, limit, offset, organisationId }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await chatAPI.listSessions(token, limit, offset, organisationId);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de chargement');
        }
    }
);

/** Charge une session complète */
export const loadChatSession = createAsyncThunk<
    ChatSession,
    { instance: any; sessionId: string },
    { state: RootState; rejectValue: string }
>(
    'chat/loadSession',
    async ({ instance, sessionId }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await chatAPI.getSession(sessionId, token);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de chargement');
        }
    }
);

/** Supprime une session */
export const deleteChatSession = createAsyncThunk<
    string, // retourne le sessionId supprimé
    { instance: any; sessionId: string },
    { state: RootState; rejectValue: string }
>(
    'chat/deleteSession',
    async ({ instance, sessionId }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            await chatAPI.deleteSession(sessionId, token);
            return sessionId;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de suppression');
        }
    }
);

/** Renomme une session */
export const renameChatSession = createAsyncThunk<
    { sessionId: string; title: string },
    { instance: any; sessionId: string; title: string },
    { state: RootState; rejectValue: string }
>(
    'chat/renameSession',
    async ({ instance, sessionId, title }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            await chatAPI.renameSession(sessionId, title, token);
            return { sessionId, title };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de renommage');
        }
    }
);

/** Charge la config LLM */
export const loadLLMConfig = createAsyncThunk<
    LLMConfigInfo,
    { instance: any },
    { state: RootState; rejectValue: string }
>(
    'chat/loadLLMConfig',
    async ({ instance }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await llmConfigAPI.getConfig(token);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de chargement config LLM');
        }
    }
);

/**
 * Sauvegarde la config LLM.
 *
 * Recharge ensuite la configuration réelle par `getConfig` plutôt que de
 * renvoyer directement la réponse de `saveConfig` : c'est `getConfig` qui
 * connaît `source`/`plan`/`usage` à jour (F3).
 */
export const saveLLMConfig = createAsyncThunk<
    LLMConfigInfo,
    LLMConfigSaveParams & { instance: any },
    { state: RootState; rejectValue: string }
>(
    'chat/saveLLMConfig',
    async ({ instance, ...params }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            await llmConfigAPI.saveConfig(params, token);
            return await llmConfigAPI.getConfig(token);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de sauvegarde');
        }
    }
);

/** Valide la clé API */
export const validateLLMConfig = createAsyncThunk<
    { valid: boolean; error?: string },
    { instance: any },
    { state: RootState; rejectValue: string }
>(
    'chat/validateLLMConfig',
    async ({ instance }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await llmConfigAPI.validateConfig(token);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de validation');
        }
    }
);

/**
 * Supprime la config LLM.
 *
 * Recharge ensuite la configuration réelle par `getConfig` plutôt que de
 * poser `{ configured: false }` à la main : un compte qui retire sa clé
 * propre retombe sur `included` s'il y a droit, plutôt que de sembler
 * privé de tout accès (spec § 4.1, F3).
 */
export const deleteLLMConfig = createAsyncThunk<
    LLMConfigInfo,
    { instance: any },
    { state: RootState; rejectValue: string }
>(
    'chat/deleteLLMConfig',
    async ({ instance }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            await llmConfigAPI.deleteConfig(token);
            return await llmConfigAPI.getConfig(token);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de suppression');
        }
    }
);

/** Charge les providers disponibles */
export const loadLLMProviders = createAsyncThunk<
    LLMProviderInfo[],
    void,
    { state: RootState; rejectValue: string }
>(
    'chat/loadLLMProviders',
    async (_, { rejectWithValue }) => {
        try {
            const data = await llmConfigAPI.getProviders();
            return data.length > 0 ? data : DEFAULT_LLM_PROVIDERS;
        } catch (error: any) {
            // Fallback silencieux : on utilise la liste par défaut côté client
            return DEFAULT_LLM_PROVIDERS;
        }
    }
);

/** Charge dynamiquement les modèles disponibles chez le provider configuré */
export const loadLLMModels = createAsyncThunk<
    ModelDiscoveryResult,
    { instance: any; provider?: string; refresh?: boolean },
    { state: RootState; rejectValue: string }
>(
    'chat/loadLLMModels',
    async ({ instance, provider, refresh }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await llmConfigAPI.getModels(token, provider, refresh);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de chargement des modèles');
        }
    }
);

/** Exécute une proposition d'action RAG Écriture */
export const executeProposal = createAsyncThunk<
    ActionExecutionResult,
    { instance: any; sessionId: string; proposalId: string },
    { state: RootState; rejectValue: string }
>(
    'chat/executeProposal',
    async ({ instance, sessionId, proposalId }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await ragActionsAPI.executeAction(sessionId, proposalId, token);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur d\'exécution');
        }
    }
);

/** Rejette une proposition d'action RAG Écriture */
export const rejectProposal = createAsyncThunk<
    { proposalId: string },
    { instance: any; sessionId: string; proposalId: string },
    { state: RootState; rejectValue: string }
>(
    'chat/rejectProposal',
    async ({ instance, sessionId, proposalId }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            await ragActionsAPI.updateProposalStatus(sessionId, proposalId, 'rejected', token);
            return { proposalId };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de rejet');
        }
    }
);

/** Exécute plusieurs propositions séquentiellement (Tout valider) */
export const executeProposalsSequentially = createAsyncThunk<
    ActionExecutionResult[],
    { instance: any; sessionId: string; proposalIds: string[] },
    { state: RootState; rejectValue: string }
>(
    'chat/executeProposalsSequentially',
    async ({ instance, sessionId, proposalIds }, { dispatch, rejectWithValue }) => {
        const results: ActionExecutionResult[] = [];
        for (const proposalId of proposalIds) {
            try {
                const result = await dispatch(executeProposal({ instance, sessionId, proposalId })).unwrap();
                results.push(result);
            } catch (error: any) {
                results.push({
                    success: false,
                    proposalId,
                    action: 'create' as any,
                    targetType: 'entree' as any,
                    message: error || 'Erreur d\'exécution'
                });
            }
        }
        return results;
    }
);

/** Rejette plusieurs propositions */
export const rejectProposalsAll = createAsyncThunk<
    { proposalIds: string[] },
    { instance: any; sessionId: string; proposalIds: string[] },
    { state: RootState; rejectValue: string }
>(
    'chat/rejectProposalsAll',
    async ({ instance, sessionId, proposalIds }, { dispatch, rejectWithValue }) => {
        for (const proposalId of proposalIds) {
            try {
                await dispatch(rejectProposal({ instance, sessionId, proposalId })).unwrap();
            } catch {
                // Continue même si un rejet échoue
            }
        }
        return { proposalIds };
    }
);

// ─── Slice ────────────────────────────────────────────────────────────

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        toggleChatPanel(state) {
            state.isOpen = !state.isOpen;
            if (!state.isOpen) state.origine = null;
        },
        setChatPanelOpen(state, action: PayloadAction<boolean>) {
            state.isOpen = action.payload;
            if (!action.payload) state.origine = null;
        },
        /** « Demander » sur un îlot (C28 SP3) : pose ou retire l'origine de la conversation. */
        setChatOrigine(state, action: PayloadAction<OrigineDuPlateau | null>) {
            state.origine = action.payload;
        },
        /** « Demander sur ce document » (C19) : pose ou retire la portée. */
        setChatScope(state, action: PayloadAction<ChatScope | null>) {
            state.scope = action.payload;
        },
        setActiveSession(state, action: PayloadAction<string | null>) {
            state.activeSessionId = action.payload;
            state.origine = null;
            if (!action.payload) {
                state.activeMessages = [];
                state.activeTitle = '';
            }
        },
        startNewConversation(state) {
            state.activeSessionId = null;
            state.origine = null;
            state.activeMessages = [];
            state.activeTitle = '';
            state.sendStatus = LOADING_STATES.IDLE;
            state.sendError = undefined;
        },
        clearSendError(state) {
            state.sendError = undefined;
            state.sendErrorCode = undefined;
            state.sendStatus = LOADING_STATES.IDLE;
        },
        setKnowledgeMode(state, action: PayloadAction<KnowledgeMode>) {
            state.knowledgeMode = action.payload;
        },
        setWriteMode(state, action: PayloadAction<boolean>) {
            state.writeMode = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── sendChatMessage ──
            .addCase(sendChatMessage.pending, (state, action) => {
                state.sendStatus = LOADING_STATES.LOADING;
                state.sendError = undefined;
                state.sendErrorCode = undefined;
                state.sendErrorUsage = undefined;

                // Écho optimiste : afficher la question immédiatement,
                // sans attendre la réponse serveur.
                const { query, displayQuery } = action.meta.arg;
                state.activeMessages.push({
                    role: 'user',
                    content: displayQuery || query,
                    timestamp: new Date().toISOString(),
                });
            })
            .addCase(sendChatMessage.fulfilled, (state, action) => {
                state.sendStatus = LOADING_STATES.SUCCEEDED;
                const isNewSession = state.activeSessionId !== action.payload.sessionId;
                state.activeSessionId = action.payload.sessionId;
                state.activeTitle = action.payload.sessionTitle;
                state.lastAnswer = action.payload.answer;
                state.lastStats = action.payload.stats;

                // Le message user est déjà affiché (écho optimiste au pending) —
                // lui rattacher les pièces jointes traitées par le serveur
                // (y compris les fichiers écartés, processingMode 'skipped').
                const lastUser = state.activeMessages[state.activeMessages.length - 1];
                if (lastUser?.role === 'user' && action.payload.attachments?.length) {
                    lastUser.attachments = action.payload.attachments;
                }

                // Puis ajouter la réponse de l'assistant, avec son rapport de
                // contexte (ce qui a réellement été injecté dans le prompt).
                state.activeMessages.push({
                    role: 'assistant',
                    content: stripActionProposalBlocks(action.payload.answer),
                    citations: action.payload.citations,
                    actionProposals: action.payload.actionProposals,
                    contextReport: action.payload.contextReport,
                    timestamp: new Date().toISOString(),
                    tokensUsed: action.payload.stats?.tokensEstimated,
                    stats: action.payload.stats,
                });

                // Consommation de la clé incluse (C18) : le compteur baisse sans recharger la config
                const assistant = action.payload.stats?.assistant;
                if (assistant?.source === 'included' && assistant.usage && state.llmConfig) {
                    state.llmConfig = { ...state.llmConfig, source: 'included', usage: assistant.usage };
                }

                // Ajouter la nouvelle session à la liste pour qu'elle apparaisse immédiatement
                if (isNewSession && !state.sessions.some(s => s.id === action.payload.sessionId)) {
                    state.sessions.unshift({
                        id: action.payload.sessionId,
                        title: action.payload.sessionTitle,
                        messageCount: state.activeMessages.length,
                        provider: action.payload.provider,
                        model: action.payload.model,
                        updatedAt: new Date().toISOString(),
                    });
                    state.sessionsTotal += 1;
                }
            })
            .addCase(sendChatMessage.rejected, (state, action) => {
                state.sendStatus = LOADING_STATES.FAILED;
                state.sendError = action.payload || 'Erreur inconnue';
                // Un arrêt par l'utilisateur (`.abort()`) rejette sans payload ni
                // meta de valeur : le code « canceled » le distingue d'un échec.
                state.sendErrorCode = action.meta.aborted ? 'canceled' : action.meta.code;
                state.sendErrorUsage = action.meta.aborted ? undefined : action.meta.usage;

                // Retirer l'écho optimiste : ChatPanel restaure la saisie dans
                // l'input pour que l'utilisateur puisse corriger/réessayer.
                const last = state.activeMessages[state.activeMessages.length - 1];
                if (last?.role === 'user') {
                    state.activeMessages.pop();
                }
            })

            // ── loadChatSessions ──
            .addCase(loadChatSessions.pending, (state) => {
                state.sessionsStatus = LOADING_STATES.LOADING;
            })
            .addCase(loadChatSessions.fulfilled, (state, action) => {
                state.sessionsStatus = LOADING_STATES.SUCCEEDED;
                state.sessionsTotal = action.payload.total;
                if (action.meta.arg.append) {
                    // Page suivante (Charger plus) : concaténer sans doublon —
                    // une session déjà affichée peut réapparaître si la liste a
                    // bougé (nouvelle conversation) entre deux pages.
                    const known = new Set(state.sessions.map(s => s.id));
                    state.sessions.push(...action.payload.sessions.filter(s => !known.has(s.id)));
                } else {
                    state.sessions = action.payload.sessions;
                }
            })
            .addCase(loadChatSessions.rejected, (state) => {
                state.sessionsStatus = LOADING_STATES.FAILED;
            })

            // ── loadChatSession ──
            .addCase(loadChatSession.fulfilled, (state, action) => {
                state.activeSessionId = action.payload._id;
                state.activeTitle = action.payload.title;
                state.activeMessages = action.payload.messages;
            })

            // ── renameChatSession ──
            .addCase(renameChatSession.fulfilled, (state, action) => {
                const { sessionId, title } = action.payload;
                const session = state.sessions.find(s => s.id === sessionId);
                if (session) session.title = title;
                if (state.activeSessionId === sessionId) {
                    state.activeTitle = title;
                }
            })

            // ── deleteChatSession ──
            .addCase(deleteChatSession.fulfilled, (state, action) => {
                state.sessions = state.sessions.filter(s => s.id !== action.payload);
                state.sessionsTotal = Math.max(0, state.sessionsTotal - 1);
                if (state.activeSessionId === action.payload) {
                    state.activeSessionId = null;
                    state.activeMessages = [];
                    state.activeTitle = '';
                }
            })

            // ── loadLLMConfig ──
            .addCase(loadLLMConfig.pending, (state) => {
                state.llmConfigStatus = LOADING_STATES.LOADING;
            })
            .addCase(loadLLMConfig.fulfilled, (state, action) => {
                state.llmConfigStatus = LOADING_STATES.SUCCEEDED;
                state.llmConfig = action.payload;
            })
            .addCase(loadLLMConfig.rejected, (state) => {
                state.llmConfigStatus = LOADING_STATES.FAILED;
            })

            // ── saveLLMConfig ──
            .addCase(saveLLMConfig.fulfilled, (state, action) => {
                state.llmConfig = action.payload;
                state.llmConfigStatus = LOADING_STATES.SUCCEEDED;
            })

            // ── validateLLMConfig ──
            .addCase(validateLLMConfig.pending, (state) => {
                state.llmValidating = true;
                state.llmValidationResult = undefined;
            })
            .addCase(validateLLMConfig.fulfilled, (state, action) => {
                state.llmValidating = false;
                state.llmValidationResult = action.payload;
            })
            .addCase(validateLLMConfig.rejected, (state) => {
                state.llmValidating = false;
            })

            // ── deleteLLMConfig ──
            .addCase(deleteLLMConfig.fulfilled, (state, action) => {
                state.llmConfig = action.payload;
                state.llmValidationResult = undefined;
            })

            // ── loadLLMProviders ──
            .addCase(loadLLMProviders.fulfilled, (state, action) => {
                state.llmProviders = action.payload;
            })

            // ── loadLLMModels ──
            .addCase(loadLLMModels.pending, (state) => {
                state.llmModelsStatus = LOADING_STATES.LOADING;
            })
            .addCase(loadLLMModels.fulfilled, (state, action) => {
                state.llmModelsStatus = LOADING_STATES.SUCCEEDED;
                state.llmModels = action.payload;
            })
            .addCase(loadLLMModels.rejected, (state) => {
                state.llmModelsStatus = LOADING_STATES.FAILED;
            })

            // ── executeProposal ──
            .addCase(executeProposal.pending, (state, action) => {
                state.executingProposalId = action.meta.arg.proposalId;
            })
            .addCase(executeProposal.fulfilled, (state, action) => {
                state.executingProposalId = null;
                // Mettre à jour le statut de la proposition dans les messages locaux
                const proposalId = action.payload.proposalId;
                for (const msg of state.activeMessages) {
                    if (msg.actionProposals) {
                        const proposal = msg.actionProposals.find(p => p.id === proposalId);
                        if (proposal) {
                            proposal.status = action.payload.success ? 'executed' : 'error';
                            // Nécessaire pour la carte de lot (Task 13) : le bouton
                            // « Ouvrir la revue » a besoin du resultId sans attendre
                            // un rechargement de session (déjà persisté côté backend).
                            if (action.payload.resultId) {
                                proposal.resultId = action.payload.resultId;
                            }
                            // Message de résultat (ex: « Lot créé : N élément(s) à
                            // examiner », ou le motif d'un refus) — affiché sur la carte.
                            if (action.payload.message) {
                                proposal.resultMessage = action.payload.message;
                            }
                            // Taille du lot : miroir local du champ persisté côté serveur
                            const resolvedCount = (action.payload.resultData as any)?.resolvedCount;
                            if (typeof resolvedCount === 'number') {
                                proposal.resultCount = resolvedCount;
                            }
                            break;
                        }
                    }
                }
            })
            .addCase(executeProposal.rejected, (state) => {
                state.executingProposalId = null;
            })

            // ── rejectProposal ──
            .addCase(rejectProposal.fulfilled, (state, action) => {
                const { proposalId } = action.payload;
                for (const msg of state.activeMessages) {
                    if (msg.actionProposals) {
                        const proposal = msg.actionProposals.find(p => p.id === proposalId);
                        if (proposal) {
                            proposal.status = 'rejected';
                            break;
                        }
                    }
                }
            });
    }
});

export const {
    toggleChatPanel, setChatPanelOpen,
    setChatScope, setChatOrigine,
    setActiveSession, startNewConversation, clearSendError,
    setKnowledgeMode, setWriteMode,
} = chatSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────

export const selectChatIsOpen = (state: RootState) => state.chat.isOpen;
export const selectChatScope = (state: RootState): ChatScope | null => state.chat.scope ?? null;
export const selectChatOrigine = (state: RootState): OrigineDuPlateau | null => state.chat.origine ?? null;
/**
 * L'origine si le plateau d'origine est celui ouvert dans la fenêtre et si l'îlot existe encore (C28 SP3,
 * décision (b)) ; sinon null : rien n'est écrit à l'aveugle sur un plateau fermé ou un cadre retiré.
 */
export const selectOrigineActive = (state: RootState): OrigineDuPlateau | null => {
    const origine = state.chat.origine ?? null;
    if (!origine) return null;
    const metanotes = (state as { metanotes?: MetaNotesState }).metanotes;
    // Le plateau doit être à l'écran : hors de la section, son enregistrement automatique n'est pas monté.
    const actif = metanotes?.affiche ? metanotes.actif : null;
    if (!actif || actif._id !== origine.metanoteId) return null;
    return actif.ilots.some(i => i.id === origine.ilotId) ? origine : null;
};
export const selectChatSessions = (state: RootState) => state.chat.sessions;
export const selectChatSessionsTotal = (state: RootState) => state.chat.sessionsTotal;
export const selectChatSessionsStatus = (state: RootState) => state.chat.sessionsStatus;
/** Aucune conversation, liste lue (ou illisible) : seule condition des questions prêtes de l'écran vide (C24 SP2,
 *  décision 3). Avant la lecture de la liste, on ne sait pas : non, pour ne pas les faire clignoter. */
export const selectAucuneConversation = (state: RootState): boolean =>
    (state.chat.sessionsStatus === LOADING_STATES.SUCCEEDED || state.chat.sessionsStatus === LOADING_STATES.FAILED)
    && state.chat.sessionsTotal === 0 && state.chat.sessions.length === 0;
export const selectActiveSessionId = (state: RootState) => state.chat.activeSessionId;
export const selectActiveMessages = (state: RootState) => state.chat.activeMessages;
export const selectActiveTitle = (state: RootState) => state.chat.activeTitle;
export const selectSendStatus = (state: RootState) => state.chat.sendStatus;
export const selectSendError = (state: RootState) => state.chat.sendError;
export const selectSendErrorCode = (state: RootState) => state.chat.sendErrorCode;
export const selectLLMConfig = (state: RootState) => state.chat.llmConfig;
export const selectLLMConfigStatus = (state: RootState) => state.chat.llmConfigStatus;
/** Abonnement (C18 SP2) : pilote seul l'affichage des boutons d'achat et de gestion */
export const selectBilling = (state: RootState) => state.chat.llmConfig?.billing;
export const selectLLMProviders = (state: RootState) => state.chat.llmProviders;
export const selectLLMValidating = (state: RootState) => state.chat.llmValidating;
export const selectLLMModels = (state: RootState) => state.chat.llmModels;
export const selectLLMModelsStatus = (state: RootState) => state.chat.llmModelsStatus;
export const selectLLMValidationResult = (state: RootState) => state.chat.llmValidationResult;
export const selectIsLLMConfigured = (state: RootState) => state.chat.llmConfig?.configured === true;
/** Provenance de la clé (C18) : `own`, `included`, `null` (rien), `undefined` (config pas encore chargée) */
export const selectLLMSource = (state: RootState) => state.chat.llmConfig?.source;
/** Consommation du mois de la clé incluse (C18) ; absente en BYOK */
export const selectAssistantUsage = (state: RootState) => state.chat.llmConfig?.usage;
/** La clé propre est une option du forfait Plus (C18 SP3) : pilote les entrées « Ma clé » et « Ajouter ma propre clé » */
export const selectOwnKeyAllowed = (state: RootState) => ownKeyAllowed(state.chat.llmConfig);
export const selectSendErrorUsage = (state: RootState) => state.chat.sendErrorUsage;
export const selectKnowledgeMode = (state: RootState) => state.chat.knowledgeMode;
/**
 * Mode de connaissance vu par l'assistant mobile (C11 SP4) : deux modes
 * seulement, « Mes notes » (strict) et « Élargi » (free). Le mode Enrichi du
 * bureau n'existe pas sur mobile et retombe sur strict.
 */
export const selectMobileKnowledgeMode = (state: RootState): 'strict' | 'free' =>
    state.chat.knowledgeMode === 'free' ? 'free' : 'strict';
export const selectWriteMode = (state: RootState) => state.chat.writeMode;
export const selectExecutingProposalId = (state: RootState) => state.chat.executingProposalId;

export default chatSlice.reducer;
